import React from "react";
import { capitalizeFirstLetter, getUser } from "../../../utils/commonfunction";
import { useTranslation } from "react-i18next";
import AvatarImage from "./AvatarImage";

function SellerSidebar({ data }) {
  const { t } = useTranslation();
  const currentUser = getUser();
  const isOwner = currentUser?._id && data?._id && String(currentUser._id) === String(data._id);
  const isProfileHidden = data?.is_profile_public === false && !isOwner;

  const profileImage = isProfileHidden ? null : (data?.user_profile_image || data?.profile_image);
  const displayName = data?.name || data?.store_name || "-";

  const SOCIAL_PLATFORMS = [
    { key: "facebook", label: "Facebook", icon: "/images/s-icon02.svg" },
    { key: "instagram", label: "Instagram", icon: "/images/s-icon01.svg" },
    { key: "twitch", label: "Twitch", icon: "/images/twitch.png" },
    { key: "linkedin", label: "LinkedIn", icon: "/images/linkdin.png" },
    { key: "youtube", label: "Youtube", icon: "/images/s-icon03.svg" },
    { key: "website", label: "Website", icon: "/images/globe.svg" },
  ];

  return (
    <div className="w-full bg-gray-50 p-[20px] rounded-[16px] text-center shadow-sm border border-gray-100">
      {/* Profile Image */}
      <div className="avatar mx-auto mb-4 bg-white rounded-[10px] p-[10px] w-full flex flex-col items-center gap-[10px] md:gap-[20px] shadow-sm">
        <div className="relative min-w-[110px] min-h-[140px] flex flex-col items-center justify-center mx-auto">
          <AvatarImage
            src={profileImage}
            name={displayName}
            alt={displayName}
            className="w-[80px] h-[80px] object-cover rounded-full border-2 border-primary-50 shadow-sm"
            style={{ fontSize: "32px" }}
          />
        
        <h1 className="headings-h1 mt-2 text-zinc-950"> {capitalizeFirstLetter(displayName)} </h1>
        </div>
      </div>

      {/* Business Profile Name */}
      {data?.business_handle && (
        <div className="flex flex-col text-left px-2">
          <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-1">
            {t("seller_details.business_handle", "Business Profile Name")}
          </p>
          <p className="body-lg text-zinc-950 font-semibold mb-6 break-all">
            {data.business_handle.startsWith("@") ? data.business_handle : `@${data.business_handle}`}
          </p>
        </div>
      )}

      {/* Name and Location */}
      <div className="flex flex-col text-left px-2">
        <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-1">{t("seller_details.location")}</p>
        <p className="body-lg text-zinc-950 font-semibold mb-6">
          {capitalizeFirstLetter(data?.store_location?.store_address ? data?.store_location?.store_address : "-")}
        </p>
      </div>

      {/* Seller Phone Number */}
      {(data?.kyc_mobile_number || data?.phone_number) && (
        <div className="flex flex-col text-left px-2">
          <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-1">
            {t("seller_details.seller_phone_number", "Seller Phone number")}
          </p>
          <p className="body-lg text-zinc-950 font-semibold mb-6">
            {data?.kyc_mobile_number || data?.phone_number}
          </p>
        </div>
      )}

      {/* Social Media Links */}
      <div className="px-2">
        <h3 className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider text-left mb-3">{t("seller_details.social_media")}</h3>
        <div className="space-y-3 text-left">
          {SOCIAL_PLATFORMS.map((platform) => {
            const url = data?.social_links?.[platform.key];
            if (!url) return null;

            return (
              <a
                key={platform.key}
                href={url.startsWith('http') ? url : `https://${url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between bg-white p-[12px] rounded-xl shadow-sm hover:shadow-md transition-all duration-200 group"
              >
                <div className="flex items-center gap-3">
                  <img src={platform.icon} alt={platform.label} className="w-6 h-6 object-contain group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-semibold text-zinc-700">{t(platform.label)}</span>
                </div>
                <div className="text-primary-400 group-hover:translate-x-1 transition-transform">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </a>
            );
          })}

          {(!data?.social_links || Object.values(data.social_links).every(v => !v)) && (
            <p className="text-zinc-400 text-xs italic py-4">{t("seller_details.no_social_links")}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default SellerSidebar;
